import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  Layout,
  Modal,
  Button,
  message,
  Typography,
  Divider,
  Card
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import {
  ArrowRightOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined
} from '@ant-design/icons';
import { AssurmaxContextProvider } from '../../contexts/AssurmaxContext';
import HeaderLayout from './HeaderLayout/HeaderLayout';
import useHandleResize from '../../utils/HandleResize';
import NavMenu from './NavMenu';
import RightSider from './RightSider';
import useAuthContext from '../../contexts/AuthContext';
import UpdateAlert from '../UpdateAlert/UpdateAlert';
import useThemeContext from '../../contexts/ThemeContext';
import { IHaveAQuestion } from './IHaveAQuestion';
import HelpModal from './HelpModal';
// import Agenda from './Agenda';
import BugModal from './BugModal';

const { Content, Footer, Sider } = Layout;

const getRightMargin = (isacc, rightCollapsed) => {
  if (isacc) {
    if (rightCollapsed) return '80px';
    return '200px';
  }
  return '0';
};

const StyledLayout = styled.div`
  height: 100vh;
`;

const StyledSider = styled.div`
  height: 100%;
  z-index: 10;
  box-shadow: 2px 0 6px rgba(0, 21, 41, 0.35);
  overflow-x: hidden;
  position: fixed;
  left: 0;
`;

const StyledContent = styled.div`
  margin: 70px
    ${({ isacc, rightcollapsed }) => getRightMargin(isacc, rightcollapsed)} 0
    ${({ leftcollapsed }) => (leftcollapsed ? '80px' : '270px')};
  overflow: auto;
  @media (max-width: 992px) {
    margin-left: 80px;
    margin-right: ${({ isacc }) => (isacc ? '80px' : '')};
  }

  @media (max-width: 576px) {
    margin-left: 0px;
  }
`;

const StyledFooter = styled.footer`
  padding: 8px 25px;
  text-align: center;
  margin-left: 257px;
  width: auto;
  background-color: #d6dde3;
  color: var(--textColor);
  box-shadow: 0 -1px 4px var(--borderColor);

  @media (max-width: 992px) {
    margin: 0px 80px;
  }

  @media (max-width: 576px) {
    margin-left: 0;
  }

  a {
    font-weight: bold;
    color: var(--textColor);
  }
`;

const BasicLayout = ({ children, path }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { dispatchAPI, user } = useAuthContext();
  const { leftCollapsed, setLeftCollapsed, rightCollapsed, setRightCollapsed } =
    useThemeContext();
  const [popup, setPopup] = useState();
  const { width, height } = useHandleResize();
  const [collapseWidth, setCollapseWidth] = useState(
    window.innerWidth < 576 ? 0 : 80
  );
  const [visible, setVisible] = useState(false);
  const [visibleHelp, setVisibleHelp] = useState(false);
  const [callMeBack, setCallMeBack] = useState(false);
  const [needHelp, setNeedHelp] = useState(false);
  const [contact, setContact] = useState(false);
  // const [learn, setLearn] = useState(false);
  const [describeBug, setDescribeBug] = useState(false);
  const params = new URLSearchParams(location.search);
  const question = params.get('question');
  const isACC = user?.role === 'users:SUPER-USER';

  const openModal = () => {
    setVisible(true);
  };

  const openModalHelp = () => {
    setVisibleHelp(true);
  };

  const handleCloseModalHelp = () => {
    setNeedHelp(needHelp === true ? !needHelp : needHelp);
    setDescribeBug(describeBug === true ? !describeBug : describeBug);
    setVisibleHelp(false);
  };

  const handleModalClose = () => {
    setVisible(false);
  };
  const infoSouscription = () => {
    Modal.info({
      title: 'Oups !',
      width: 500,
      content: (
        <div>
          <p>
            {`${t('basiclayout.footer.souscription')} ${t(
              'basiclayout.footer.brave'
            )}`}
          </p>
        </div>
      ),
      onOk() {}
    });
  };
  const infosAgenda = () => {
    Modal.info({
      title: 'Oups !',
      width: 500,
      content: (
        <div>
          <p>
            {`${t('basiclayout.footer.agenda')} ${t(
              'basiclayout.footer.brave'
            )}`}
          </p>
        </div>
      ),
      onOk() {}
    });
  };

  const isReductScreen = useMediaQuery({ query: '(max-width: 1350px)' });

  useEffect(() => {
    if (width < 576) {
      setCollapseWidth(0);
    } else {
      setCollapseWidth(80);
    }
    if (width < 992) {
      setLeftCollapsed(true);
      setRightCollapsed(true);
    } // eslint-disable-next-line
  }, [width, height]);

  useEffect(() => {
    document
      .getElementById('mainContent')
      .scrollTo({ behavior: 'smooth', top: 0 });
    // eslint-disable-next-line
  }, [window.location.href]);

  useEffect(() => {
    if (question === 'true') {
      (() => {
        openModal();
        setCallMeBack(!callMeBack);
      })();
      // eslint-disable-next-line
    }
  }, [question]);

  const getPopup = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: 'settings?label=POPUP'
      });
      if (data.length && data[0].value) setPopup(data[0]);
    } catch (e) {
      setPopup(undefined);
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      if (location?.from === 'login') await getPopup();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (popup) {
      Modal.info({
        title: (
          <Typography.Title level={5}>Bienvenue sur Power</Typography.Title>
        ),
        content: popup.value,
        onOk: () => setPopup(undefined)
      });
    }
  }, [popup]);

  return (
    <AssurmaxContextProvider>
      <StyledLayout as={Layout}>
        <UpdateAlert />
        <StyledSider
          as={Sider}
          width={270}
          breakpoint="lg"
          trigger={null}
          collapsible
          collapsedWidth={collapseWidth}
          collapsed={leftCollapsed}
        >
          <NavMenu path={path} setCollapsed={setLeftCollapsed} />
          <Button
            type="link"
            onClick={() => setLeftCollapsed(!leftCollapsed)}
            icon={
              leftCollapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />
            }
            style={{
              width: leftCollapsed ? collapseWidth : 270,
              position: 'fixed',
              bottom: 0,
              color: 'var(--textColorSecondary)',
              borderTop: '1px solid rgba(249,249,249,0.2)',
              borderRadius: 0,
              backgroundColor: 'var(--clientColor)'
            }}
          />
        </StyledSider>
        {isACC && (
          <RightSider
            path={path}
            setCollapsed={setRightCollapsed}
            collapsed={rightCollapsed}
          />
        )}
        <Layout>
          <HeaderLayout
            leftCollapsed={leftCollapsed}
            rightCollapsed={rightCollapsed}
            collapseWidth={collapseWidth}
            isACC={isACC}
          />
          <StyledContent
            as={Content}
            id="mainContent"
            isacc={isACC ? 1 : 0}
            leftcollapsed={leftCollapsed ? 1 : 0}
            rightcollapsed={rightCollapsed ? 1 : 0}
          >
            {children}
          </StyledContent>
          <StyledFooter
            as={Footer}
            style={{
              marginRight: getRightMargin(isACC, rightCollapsed),
              // eslint-disable-next-line no-nested-ternary
              marginLeft: width < 600 ? 0 : leftCollapsed ? 81 : 257
            }}
          >
            {user?.role !== 'guests:GUEST' && (
              <>
                <Button onClick={() => openModal()} type="primary">
                  {t(`basiclayout.footer.question`)}
                </Button>
                <Modal
                  title={t('basiclayout.footer.modal_title')}
                  open={visible}
                  onCancel={() => handleModalClose()}
                  width={isReductScreen ? 600 : 900}
                  footer={null}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: isReductScreen
                        ? 'space-between'
                        : 'space-around',
                      flexDirection: isReductScreen ? 'column' : 'row'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, marginBottom: 10 }}>
                        {t(`basiclayout.footer.modal_courageous`)}
                      </div>
                      <Button
                        type="text"
                        style={{ border: 'none', top: -5 }}
                        onClick={() => infoSouscription()}
                      >
                        <ArrowRightOutlined
                          style={{
                            display: isReductScreen ? 'none' : 'inline'
                          }}
                        />
                        {t(`basiclayout.footer.modal_guide`)}
                      </Button>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, marginBottom: 10 }}>
                        {t(`basiclayout.footer.modal_lazy`)}
                      </div>
                      <div
                        style={{
                          fontFamily: 'Oswald',
                          textTransform: 'uppercase',
                          letterSpacing: '.15em',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <ArrowRightOutlined
                          style={{
                            marginRight: 8,
                            display: isReductScreen ? 'none' : 'inline'
                          }}
                        />
                        {t(`basiclayout.footer.modal_team`)}
                        <a href="tel:0328649623" style={{ fontWeight: 'bold' }}>
                          {`: ${t(`basiclayout.footer.tel`)}`}
                        </a>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 18, marginBottom: 10 }}>
                        {t(`basiclayout.footer.modal_super_lazy`)}
                      </div>
                      <div
                        style={{
                          fontFamily: 'Oswald',
                          textTransform: 'uppercase',
                          letterSpacing: '.15em',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <ArrowRightOutlined
                          style={{
                            marginRight: 8,
                            display: isReductScreen ? 'none' : 'inline'
                          }}
                        />
                        <Button
                          type="text"
                          onClick={() => setCallMeBack(!callMeBack)}
                        >
                          {t(`basiclayout.footer.modal_callBack`)}
                        </Button>
                      </div>
                    </div>
                  </div>
                  {callMeBack && (
                    <>
                      <Divider />
                      <IHaveAQuestion
                        setCallMeBack={setCallMeBack}
                        handleModalClose={handleModalClose}
                      />
                    </>
                  )}
                </Modal>
                <Button
                  onClick={() => openModalHelp()}
                  type="primary"
                  style={{ margin: 10 }}
                >
                  {t(`basiclayout.footer.help`)}
                </Button>
                <Modal
                  title={t('basiclayout.footer.modal_title')}
                  open={visibleHelp}
                  onCancel={() => handleCloseModalHelp()}
                  width={isReductScreen ? 600 : 900}
                  footer={null}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: isReductScreen
                        ? 'space-between'
                        : 'space-around',
                      flexDirection: 'Column'
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <Button
                        type="text"
                        style={{
                          fontSize: 18,
                          marginBottom: 10,
                          border: 'none',
                          top: -5,
                          whiteSpace: 'normal'
                        }}
                        onClick={() => setNeedHelp(!needHelp)}
                      >
                        {t(`basiclayout.footer.modal_help`)}
                      </Button>
                    </div>
                    {needHelp && (
                      <Card style={{ whiteSpace: 'normal' }}>
                        <>
                          <Button
                            type="text"
                            style={{
                              border: 'none',
                              top: -5,
                              textAlign: 'left',
                              marginLeft: isReductScreen ? '0px' : '150px',
                              whiteSpace: 'normal'
                            }}
                            onClick={() => setContact(!contact)}
                          >
                            <ArrowRightOutlined
                              style={{
                                display: isReductScreen ? 'none' : 'inline',
                                fontSize: 18,
                                marginBottom: 10
                              }}
                            />
                            {t(`basiclayout.footer.modal_contact`)}
                          </Button>
                          {contact && (
                            <>
                              <HelpModal
                                setContact={setContact}
                                handleCloseModalHelp={handleCloseModalHelp}
                              />
                            </>
                          )}
                          <Button
                            type="text"
                            style={{
                              border: 'none',
                              top: -5,
                              textAlign: 'left',
                              marginLeft: isReductScreen ? '0px' : '150px',
                              whiteSpace: 'normal'
                            }}
                            onClick={() => infosAgenda()}
                          >
                            <ArrowRightOutlined
                              style={{
                                display: isReductScreen ? 'none' : 'inline',
                                fontSize: 18,
                                marginBottom: 10
                              }}
                            />
                            {t(`basiclayout.footer.modal_learning`)}
                          </Button>
                          {/* {learn && (
                          <>
                            <Agenda />
                          </>
                        )} */}
                        </>
                      </Card>
                    )}
                  </div>
                  <Divider>OU</Divider>
                  <div style={{ textAlign: 'center' }}>
                    <Button
                      type="text"
                      style={{
                        fontSize: 18,
                        marginBottom: 10,
                        border: 'none',
                        top: -5,
                        whiteSpace: 'normal'
                      }}
                      onClick={() => setDescribeBug(!describeBug)}
                    >
                      {t(`basiclayout.footer.modal_bug`)}
                    </Button>
                    {describeBug && (
                      <>
                        <BugModal
                          setDescribeBug={setDescribeBug}
                          handleCloseModalHelp={handleCloseModalHelp}
                        />
                      </>
                    )}
                  </div>
                </Modal>
              </>
            )}
          </StyledFooter>
        </Layout>
      </StyledLayout>
    </AssurmaxContextProvider>
  );
};

BasicLayout.propTypes = {
  children: PropTypes.node.isRequired,
  path: PropTypes.string.isRequired
};

export default BasicLayout;
